import bilAPI from "./api";

// `page_size` (opt-in) widens the page; the backend still caps it at 1000, so
// any picker over a roster that can exceed that must pass `search` and let the
// server narrow — a page cap silently drops the rest with no error.
export const getUsers = ({
  params: {
    search,
    role,
    page,
    observable,
    can_conduct,
    page_size,
    organization,
    ordering,
  } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/auth/users/`, {
          // `organization` narrows to one school (or a head org's subtree) —
          // the same drill-down the analytics endpoints take. `can_conduct`
          // is the observer side of `observable`: who may be named
          // main_observer. Params are listed explicitly here, so a new one is
          // dropped silently until it is named.
          params: {
            search,
            role,
            page,
            observable,
            can_conduct,
            page_size,
            organization,
            ordering,
          },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// One account by uuid. `params` takes the same filters as the list — notably
// `observable`, which the backend's get_queryset applies to retrieve as well,
// so a deep link to someone no longer observable 404s rather than resolving.
export const getUser = ({ uuid, params = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/auth/users/${uuid}/`, { params, signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Role catalog: [{uuid, code, name (localized), is_privileged}]
export const getRoles = ({ signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/auth/roles/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const createUser = ({ body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/auth/users/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const updateUser = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .patch(`/auth/users/${uuid}/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteUser = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .delete(`/auth/users/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Bulk counterpart of the per-row invite: invites every manageable account
// that cannot sign in yet (unusable password, no SSO identity) and has no
// live invitation out. `organization` narrows a multi-school admin to one
// school (and is required for superusers). Resolves to
// {invited, skipped_privileged}.
// `users` (a list of account uuids) narrows the sweep to a selection; omit it
// to invite everyone pending. Eligibility rules are the backend's either way.
export const inviteAllUsers = ({ organization, users, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(
          `/auth/users/invite-all/`,
          {
            ...(organization ? { organization } : {}),
            ...(users ? { users } : {}),
          },
          { signal },
        )
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Bulk staff import — same preview/commit contract as the timetable importer:
// `commit: false` returns the report without writing, `commit: true` applies it
// (and is refused server-side while any row errors). The .xlsx file goes up
// as-is; parsing spreadsheets is the backend's job.
export const importStaff = ({ file, commit = false, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("commit", commit ? "true" : "false");
      bilAPI
        .post(`/auth/users/import/`, form, {
          signal,
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// GET on the same endpoint serves the .xlsx template whose header the
// importer expects — generated server-side so the two can never drift.
export const downloadStaffImportTemplate = ({ signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/auth/users/import/`, { signal, responseType: "blob" })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};
